import type {
  ComponentSize,
  ComponentVariant,
  ComponentAppearance,
} from 'src/modules/shared/types/component.type';

export const tabsBaseClasses: string[] = [
  'w-full',
  'rounded-lg',
  'transition-all',
  'duration-200',
  'outline-none',
  'focus-within:outline-none',
  'focus-within:!ring-0',
  'focus-within:!ring-offset-0',
];

export const tabsContainerClasses: Record<ComponentSize, string> = {
  '2xs': 'p-0.5',
  xs: 'p-0.5',
  sm: 'p-1',
  md: 'p-1',
  lg: 'p-1.5',
};

/**
 * Single sideways-scrolling row for horizontal tabs, with the scrollbar
 * hidden. Inline-size containment keeps the tabs' combined width from
 * stretching the parent, so the row scrolls inside grid and flex layouts
 * instead of widening the page. The small inset keeps the selected tab's
 * shadow from being clipped.
 */
export const tabsScrollRowClasses =
  'relative -m-0.5 flex gap-2 overflow-x-auto p-0.5 [contain:inline-size] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

/**
 * Edge fades of a scrolling tab row, one per side that has hidden tabs. A
 * mask fades the tabs themselves, so it works on any container background.
 */
export const tabsScrollFadeClasses = {
  start: '[mask-image:linear-gradient(to_right,transparent,black_1.5rem)]',
  end: '[mask-image:linear-gradient(to_right,black_calc(100%_-_1.5rem),transparent)]',
  both: '[mask-image:linear-gradient(to_right,transparent,black_1.5rem,black_calc(100%_-_1.5rem),transparent)]',
} as const;

export const tabsAppearanceClasses: Record<
  NonNullable<ComponentAppearance>,
  Record<ComponentVariant, string>
> = {
  filled: {
    primary:
      'bg-primary-50 border border-primary-200 disabled:bg-primary-50 disabled:border-primary-200',
    secondary:
      'bg-secondary-50 border border-secondary-200 disabled:bg-secondary-50 disabled:border-secondary-200',
    danger:
      'bg-danger-50 border border-danger-200 disabled:bg-danger-50 disabled:border-danger-200',
    warning:
      'bg-warning-50 border border-warning-200 disabled:bg-warning-50 disabled:border-warning-200',
    neutral:
      'bg-neutral-50 border border-neutral-200 disabled:bg-neutral-50 disabled:border-neutral-200',
    success:
      'bg-success-50 border border-success-200 disabled:bg-success-50 disabled:border-success-200',
    info: 'bg-info-50 border border-info-200 disabled:bg-info-50 disabled:border-info-200',
    whatsapp:
      'bg-whatsapp-50 border border-whatsapp-200 disabled:bg-whatsapp-50 disabled:border-whatsapp-200',
    call: 'bg-call-50 border border-call-200 disabled:bg-call-50 disabled:border-call-200',
    email:
      'bg-email-50 border border-email-200 disabled:bg-email-50 disabled:border-email-200',
  },
  outline: {
    primary:
      'bg-white border border-primary-200 disabled:bg-white disabled:border-primary-200',
    secondary:
      'bg-white border border-secondary-200 disabled:bg-white disabled:border-secondary-200',
    danger:
      'bg-white border border-danger-200 disabled:bg-white disabled:border-danger-200',
    warning:
      'bg-white border border-warning-200 disabled:bg-white disabled:border-warning-200',
    neutral:
      'bg-white border border-neutral-200 disabled:bg-white disabled:border-neutral-200',
    success:
      'bg-white border border-success-200 disabled:bg-white disabled:border-success-200',
    info: 'bg-white border border-info-200 disabled:bg-white disabled:border-info-200',
    whatsapp:
      'bg-white border border-whatsapp-200 disabled:bg-white disabled:border-whatsapp-200',
    call: 'bg-white border border-call-200 disabled:bg-white disabled:border-call-200',
    email:
      'bg-white border border-email-200 disabled:bg-white disabled:border-email-200',
  },
  soft: {
    primary:
      'bg-primary-50 border border-transparent disabled:bg-primary-50 disabled:border-transparent',
    secondary:
      'bg-secondary-50 border border-transparent disabled:bg-secondary-50 disabled:border-transparent',
    danger:
      'bg-danger-50 border border-transparent disabled:bg-danger-50 disabled:border-transparent',
    warning:
      'bg-warning-50 border border-transparent disabled:bg-warning-50 disabled:border-transparent',
    neutral:
      'bg-neutral-50 border border-transparent disabled:bg-neutral-50 disabled:border-transparent',
    success:
      'bg-success-50 border border-transparent disabled:bg-success-50 disabled:border-transparent',
    info: 'bg-info-50 border border-transparent disabled:bg-info-50 disabled:border-transparent',
    whatsapp:
      'bg-whatsapp-50 border border-transparent disabled:bg-whatsapp-50 disabled:border-transparent',
    call: 'bg-call-50 border border-transparent disabled:bg-call-50 disabled:border-transparent',
    email:
      'bg-email-50 border border-transparent disabled:bg-email-50 disabled:border-transparent',
  },
  ghost: {
    primary:
      'bg-transparent border border-transparent disabled:bg-transparent disabled:border-transparent',
    secondary:
      'bg-transparent border border-transparent disabled:bg-transparent disabled:border-transparent',
    danger:
      'bg-transparent border border-transparent disabled:bg-transparent disabled:border-transparent',
    warning:
      'bg-transparent border border-transparent disabled:bg-transparent disabled:border-transparent',
    neutral:
      'bg-transparent border border-transparent disabled:bg-transparent disabled:border-transparent',
    success:
      'bg-transparent border border-transparent disabled:bg-transparent disabled:border-transparent',
    info: 'bg-transparent border border-transparent disabled:bg-transparent disabled:border-transparent',
    whatsapp:
      'bg-transparent border border-transparent disabled:bg-transparent disabled:border-transparent',
    call: 'bg-transparent border border-transparent disabled:bg-transparent disabled:border-transparent',
    email:
      'bg-transparent border border-transparent disabled:bg-transparent disabled:border-transparent',
  },
};
